// Single source of truth for the plan "sections" a master admin can switch on
// per plan (Master > Plans). The same list drives the Plan Config checkboxes,
// the route guard and the sidebar filter.
//
// `value` must match the first path segment of the route it unlocks, because
// that is what SidePanel/PrivateRoute compare against. Keep them in sync when
// adding a screen, otherwise the menu item can never be enabled by any plan.
export const PLAN_SECTIONS = [
  // Public website
  { label: 'Public', value: 'public' },
  { label: 'Web', value: 'web' },
  { label: 'Classical Homeopathy', value: 'classical_homeopathy' },
  { label: 'Treatment Package', value: 'treatmentPackage' },
  { label: 'Alternative Therapies', value: 'alternative_therapies' },
  { label: 'Health Packages', value: 'health_packages' },
  { label: 'Testimonials (Website Page)', value: 'testimonials' },
  { label: 'Online Clinic', value: 'online_clinic' },
  { label: 'Contact Us (Website Page)', value: 'contact_us' },
  { label: 'Privacy Policy', value: 'privacy_policy' },
  { label: 'Feedback', value: 'feedback' },
  { label: 'Popup', value: 'popup' },
  { label: 'Agreement', value: 'aggrement' },
  { label: 'Payment', value: 'payment' },

  // Core clinic
  { label: 'Dashboard', value: 'dashboard' },
  { label: 'Enquiry', value: 'enquiry' },
  { label: 'Appointment', value: 'appointment' },
  { label: 'Video Consultation', value: 'video-consultation' },
  { label: 'Prescription', value: 'prescription' },
  { label: 'Referral Business', value: 'referral-business' },
  { label: 'Patients', value: 'patients' },
  { label: 'Vitals Templates', value: 'vitals-templates' },
  { label: 'Courier', value: 'courier' },
  { label: 'Transaction', value: 'transaction' },
  { label: 'Income Expenses', value: 'income_expenses' },
  { label: 'Inventory', value: 'main' },
  { label: 'Graphs', value: 'graphs' },

  // Staff
  { label: 'Main Doctor', value: 'main-doctor' },
  { label: 'Staff Profile', value: 'staffprofile' },
  { label: 'Doctors', value: 'doctors' },
  { label: 'Assistant Doctor', value: 'assistantDoctor' },
  { label: 'Receptionist', value: 'receptionist' },
  { label: 'Consultant', value: 'consultant' },
  { label: 'Clinic Profile', value: 'clinic-profile' },
  { label: 'User Profile', value: 'login-profile' },
  { label: 'Subscription Details', value: 'subscription' },

  // Attendance
  { label: 'Mark Attendance', value: 'attendancecount' },
  { label: 'Leave Requests', value: 'attendancerequests' },
  { label: 'Leave Calendar', value: 'leavecalendar' },
  { label: 'Attendance Calendar', value: 'attendancecalendar' },
  { label: 'Calendar', value: 'calendar' },
  { label: 'Certificates', value: 'certificates' },

  // Therapies & bids
  { label: 'Alternative Therapies Page', value: 'AlternativeTherapies' },
  { label: 'Bids', value: 'Bids' },

  // Interactions (landing-page form submissions)
  { label: 'Contact Us (Submissions)', value: 'ContactUs' },
  { label: 'Call Back', value: 'CallBack' },
  { label: 'Questions', value: 'Questions' },
  { label: 'Testimonials (Submissions)', value: 'Testimonials' },
  { label: 'Shared Thoughts', value: 'SharedThoughts' },
  { label: 'Enrolled In Health Packages', value: 'EnrolledInHealthPackages' },
  { label: 'Calm Your Ailment Naturally', value: 'CalmYourEczemaNaturally' },
  { label: 'Book Alternative Therapies', value: 'BookAlternativeTherapies' },

  // Settings & content
  { label: 'Drop Downs', value: 'DropDowns' },
  { label: 'Site Config', value: 'site-config' },
];

export const PLAN_SECTION_VALUES = PLAN_SECTIONS.map((s) => s.value);

// Display grouping for the screens that render the whole list as checkboxes
// (Plans, Subscription Plan, Permissions). Order here is the order they render.
export const SECTION_GROUPS = [
  { title: 'Core Clinic & Patients', values: ['dashboard', 'enquiry', 'appointment', 'video-consultation', 'prescription', 'patients', 'vitals-templates', 'courier'] },
  { title: 'Finance & Inventory', values: ['transaction', 'income_expenses', 'referral-business', 'main', 'graphs', 'payment'] },
  { title: 'Staff & Profile', values: ['main-doctor', 'staffprofile', 'doctors', 'assistantDoctor', 'receptionist', 'consultant', 'clinic-profile', 'login-profile', 'subscription'] },
  { title: 'Operations & Attendance', values: ['attendancecount', 'attendancerequests', 'attendancecalendar', 'leavecalendar', 'calendar', 'certificates'] },
  { title: 'Therapies & Bids', values: ['alternative_therapies', 'AlternativeTherapies', 'Bids', 'health_packages', 'treatmentPackage', 'classical_homeopathy'] },
  { title: 'Interactions', values: ['feedback', 'ContactUs', 'CallBack', 'Questions', 'Testimonials', 'SharedThoughts', 'EnrolledInHealthPackages', 'CalmYourEczemaNaturally', 'BookAlternativeTherapies'] },
  { title: 'Website & Settings', values: ['public', 'web', 'online_clinic', 'contact_us', 'testimonials', 'privacy_policy', 'popup', 'aggrement', 'DropDowns', 'site-config'] },
];

// Every section, grouped, with any value missing from SECTION_GROUPS swept into
// "Other" so adding to PLAN_SECTIONS alone can never hide a checkbox.
export const groupedPlanSections = () => {
  const placed = new Set(SECTION_GROUPS.flatMap((g) => g.values));
  const byValue = Object.fromEntries(PLAN_SECTIONS.map((s) => [s.value, s]));

  const groups = SECTION_GROUPS.map((g) => ({
    title: g.title,
    sections: g.values.map((v) => byValue[v]).filter(Boolean),
  })).filter((g) => g.sections.length > 0);

  const leftovers = PLAN_SECTIONS.filter((s) => !placed.has(s.value));
  if (leftovers.length) groups.push({ title: 'Other', sections: leftovers });

  return groups;
};

export default PLAN_SECTIONS;

export const FULL_ACCESS_ROLES = ['MainDoctor', 'maindoctor', 'main_doctor', 'admin', 'Admin', 'MasterAdmin', 'SuperAdmin'];

export const SEGMENT_SECTIONS = {
  permissions: 'login-profile',
  inventory: 'main',
  patient: 'patients',
  'patient-search': 'patients',
  appointments: 'appointment',
  mainDoctor: 'staffprofile',
  'main-doctor': 'staffprofile',
};

export const UNGATED_SEGMENTS = [
  '',
  'login',
  'signup',
  'public',
  'reset-password',
  'track',
  'plans',
  'afterAction',
];

export const sectionForPath = (pathname) => {
  const segment = String(pathname || '').split('?')[0].split('/').filter(Boolean)[0] || '';
  if (UNGATED_SEGMENTS.includes(segment)) return null;
  const section = SEGMENT_SECTIONS[segment] || segment;
  return PLAN_SECTION_VALUES.includes(section) ? section : null;
};

// Roles that must be explicitly granted sections — they get NOTHING by default.
// Any role not in FULL_ACCESS_ROLES and not listed here still falls through to
// full access, preserving backward-compat for any future roles we haven't categorised.
export const RESTRICTED_ROLES = ['Consultant', 'Doctor', 'AssistantDoctor', 'Receptionist'];

export const resolveAllowedSections = ({ role, planSections, permissions } = {}) => {
  const normalizedRole = String(role || '').toLowerCase().replace(/[_\s-]/g, '');
  const isFullAccess = FULL_ACCESS_ROLES.some(
    (r) => r.toLowerCase().replace(/[_\s-]/g, '') === normalizedRole
  );

  // If role is MainDoctor / admin / SuperAdmin — show EVERYTHING unconditionally
  if (isFullAccess) return PLAN_SECTION_VALUES;

  const plan = Array.isArray(planSections) && planSections.length ? planSections : null;
  const granted = Array.isArray(permissions) && permissions.length ? permissions : null;

  // Staff roles (Consultant, Doctor, etc.) — default to NO access.
  // They must have sections explicitly granted via the Permissions screen.
  if (RESTRICTED_ROLES.includes(role)) {
    if (!granted) return [];             // nothing granted → nothing visible
    if (!plan) return granted;           // granted but no plan cap → use granted
    return plan.filter((v) => granted.includes(v)); // both → intersection
  }

  // Any other role: preserve existing behaviour (full access if unconfigured).
  if (!plan && !granted) return PLAN_SECTION_VALUES;
  if (plan && granted) return plan.filter((value) => granted.includes(value));
  return plan || granted || PLAN_SECTION_VALUES;
};
